import { Fragment, FunctionComponent, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import moment, { Moment } from "moment";

import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";
import { VirtualScroller, VirtualScrollerLazyEvent } from "primereact/virtualscroller";

import { faRotate } from "@fortawesome/pro-regular-svg-icons";

import { DateIs } from "../../../../../utils/date-utils";
import { ActionButton } from "../../../../components/Shared/Buttons";
import { translationKeys } from "../../../../models/app-translation-keys";
import { IChatThreadExtendedModel } from "../../../../models/interfaces";
import { useThreadService } from "../../../../services/ServiceHooks";
import { ICustomKnowledgeBaseReadChatbot } from "../../../../stores";
import { useStore } from "../../../../stores/StoreContext";
import { Separator } from "./Separator/Separator";
import { ThreadItem } from "./ThreadItem/ThreadItem";
import { useThreadHistoryVirtualItemSize } from "./useThreadHistoryVirtualItemSize";

import "./ThreadHistoryList.scss";

interface IThreadHistoryList {
  threads: IChatThreadExtendedModel[];
  knowledgeBases: ICustomKnowledgeBaseReadChatbot[];
  activeThreadId?: string;
  showDateSeparators?: boolean;
}

const RawThreadHistoryList: FunctionComponent<IThreadHistoryList> = (props: IThreadHistoryList) => {
  const { t } = useTranslation();
  const { threadStore, sidebarStore } = useStore();
  const threadService = useThreadService();

  const virtualScrollerRef = useRef<VirtualScroller | null>(null);
  const virtualItemSize = useThreadHistoryVirtualItemSize({
    virtualScrollerRef,
    showDateSeparators: props.showDateSeparators,
    isSidebarCollapsed: sidebarStore.isCollapsed,
    isOverlaySidebarVisible: sidebarStore.isOverlaySidebarVisible,
  });

  const separatorKeys = useMemo(() => {
    const dateChecksMap: Map<string, { used: boolean; func: (date: Moment) => boolean }> = new Map([
      ["today", { used: false, func: DateIs.today }],
      ["yesterday", { used: false, func: DateIs.yesterday }],
      ["lastWeek", { used: false, func: DateIs.lastWeek }],
      ["p30Days", { used: false, func: DateIs.previous30Days }],
      ["currentYear", { used: false, func: DateIs.currentYear }],
      ["beforeCurrentYear", { used: false, func: DateIs.beforeCurrentYear }],
    ]);

    return props.threads.map(thread => {
      const threadDate = moment(thread.lastActivity);

      for (const [key, dateCheck] of dateChecksMap) {
        if (!dateCheck.used && dateCheck.func(threadDate)) {
          dateCheck.used = true;
          return key;
        }
      }

      return undefined;
    });
  }, [props.threads]);

  const retryThreadLoad = async () => {
    await threadService.retryFailedThreadPage();
  };

  const threadLoadError = !!threadStore.hasThreadLoadError;

  const threadsById = useMemo(
    () => new Map(threadStore.threads.map(thread => [thread.id, thread])),
    [threadStore.threads],
  );

  const loadMoreThreads = async () => {
    if (!threadStore.hasMoreThreads || threadStore.isLoadingMoreThreads || threadLoadError) {
      return;
    }

    await threadService.loadNextThreads();
  };

  const handleLazyLoad = async (event: VirtualScrollerLazyEvent) => {
    if (
      event.last == null ||
      Number.isNaN(Number(event.last)) ||
      threadStore.isLoadingMoreThreads ||
      !threadStore.hasMoreThreads ||
      threadLoadError
    ) {
      return;
    }

    const remainingItemsAfterViewport = props.threads.length - Number(event.last);
    if (remainingItemsAfterViewport > 2) {
      return;
    }

    await loadMoreThreads();
  };

  const threadItemTemplate = (thread: IChatThreadExtendedModel, index: number) => {
    const updatedThread = threadsById.get(thread.id) || thread;
    const separatorKey = separatorKeys[index];
    const knowledgeBase = props.knowledgeBases.find(kb => kb.id === updatedThread.knowledgeBaseId);
    const isActive = updatedThread.id === props.activeThreadId;

    return (
      <div className="thread-list-row">
        {props.showDateSeparators && separatorKey && (
          <div
            className={classNames("thread-list-row-separator", {
              "thread-list-row-separator-spaced": index > 0,
            })}
          >
            <Separator separatorKey={separatorKey} />
          </div>
        )}

        <div className={classNames("thread", { active: isActive })}>
          <ThreadItem thread={updatedThread} knowledgeBase={knowledgeBase} isActive={isActive} />
        </div>
      </div>
    );
  };

  const loadingFooterMemo = useMemo(
    () =>
      threadStore.isLoadingMoreThreads ? (
        <div className="thread-list-loading-footer">
          <ProgressSpinner className="small-inline-spinner thread-list-loading-indicator" />
        </div>
      ) : undefined,
    [threadStore.isLoadingMoreThreads],
  );

  if (!props.threads.length) {
    if (threadLoadError) {
      return (
        <div className="thread-history-error" role="alert">
          <span>{t(translationKeys.sidebarThreads.loadError)}</span>

          <ActionButton
            id="retry-thread-history-button"
            label={t(translationKeys.actions.tryAgain)}
            icon={faRotate}
            onClick={retryThreadLoad}
            showBackground
            fullWidth
            centeredHorizontally
          />
        </div>
      );
    }

    return <span className="no-threads">{t(translationKeys.sidebarThreads.noThreads)}</span>;
  }

  return (
    <Fragment>
      <VirtualScroller
        ref={virtualScrollerRef}
        items={props.threads}
        itemSize={virtualItemSize}
        lazy
        numToleratedItems={2}
        onLazyLoad={handleLazyLoad}
        className={classNames("thread-list thread-list-virtual-scroller", {
          "virtual-scroller-scroll-locked": threadStore.isLoadingMoreThreads,
        })}
        itemTemplate={(thread: IChatThreadExtendedModel, options: { index: number }) =>
          threadItemTemplate(thread, options.index)
        }
      />

      <div className="thread-list-footer" aria-live="polite">
        {threadStore.isLoadingMoreThreads && loadingFooterMemo}

        {threadLoadError && (
          <ActionButton
            id="retry-thread-page-button"
            label={t(translationKeys.actions.tryAgain)}
            icon={faRotate}
            onClick={retryThreadLoad}
            showBackground
            fullWidth
            centeredHorizontally
          />
        )}
      </div>
    </Fragment>
  );
};

export const ThreadHistoryList = observer(RawThreadHistoryList);
